import { Component, EventEmitter, OnInit, Output, computed, effect, inject, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../../core/services/api.service';
import { CrmProductRow } from '../../deck-admin.models';
import { PaginationState } from '../../deck-list-state';
import { productStamp, resolveProductRow, toProductContext } from '../../selected-product.util';
import { PaginationComponent } from '../../../../../shared/components/pagination/pagination.component';
import { FOR_ENV_OPTIONS, envIncludesProduction, forEnvBadgeClass, forEnvLabel } from '../../for-env.util';
import { AgentToolService } from './agent-tool.service';
import { AgentTool } from './agent-tool.models';
import { AgentPromptService } from '../prompts/agent-prompt.service';
import { ToolFormPanelComponent, ToolPanelMode } from './components/tool-form-panel/tool-form-panel.component';

/**
 * Agent Tools tab: a table of the selected product's live-call agent tools.
 *
 * Product scope mirrors the deck board and the prompts tab — the tab reacts
 * to the CRM's global product selection, resolves it, and points
 * {@link AgentToolService} at that product. {@link AgentPromptService} is
 * scoped alongside it purely so the form panel's `app-agent-multi-select`
 * (which loads prompts through that service) sees the right product too.
 */
@Component({
  selector: 'app-agent-tool',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, ToolFormPanelComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './agent-tool.component.html',
})
export class AgentToolComponent implements OnInit {
  private toolService = inject(AgentToolService);
  private agentPromptService = inject(AgentPromptService);
  private apiService = inject(ApiService);

  /** Emits the tool count after each load so the tab shell can badge it. */
  @Output() countChange = new EventEmitter<number>();

  tools = signal<AgentTool[]>([]);
  loading = signal(false);
  errorMsg = signal('');
  includeInactive = signal(true);

  // Panel
  panelOpen = signal(false);
  panelMode = signal<ToolPanelMode>('create');
  selectedTool = signal<AgentTool | null>(null);

  // Delete
  deleteTarget = signal<AgentTool | null>(null);
  deleting = signal(false);

  // Environment move (Test / Production / Both), pending the confirm dialog.
  envChangeTarget = signal<{ tool: AgentTool; to: number } | null>(null);
  changingEnv = signal(false);

  // Env selector helpers for the template.
  readonly envOptions = FOR_ENV_OPTIONS;
  readonly envLabel = forEnvLabel;
  readonly envBadgeClass = forEnvBadgeClass;
  readonly envIncludesProduction = envIncludesProduction;

  readonly pager = new PaginationState(10);
  paginatedTools = computed(() => this.pager.slice(this.tools()));

  /** Flips once /product/list-product has answered (or failed). */
  private productsReady = signal(false);
  private appliedProductStamp: string | undefined;

  activeProduct = computed<CrmProductRow | null>(() =>
    resolveProductRow(
      (this.apiService.products() ?? []) as CrmProductRow[],
      this.apiService.selectedProductId()
    )
  );

  constructor() {
    effect(() => {
      if (!this.productsReady()) return;
      const product = this.activeProduct();
      untracked(() => this.applyProductScope(product));
    });
  }

  ngOnInit(): void {
    this.apiService.fetchProducts().subscribe({
      next: () => this.productsReady.set(true),
      error: () => this.productsReady.set(true),
    });
  }

  /** Point both services at `product`'s scope and reload. No-op if scope is unchanged. */
  private applyProductScope(product: CrmProductRow | null): void {
    const stamp = productStamp(product);
    if (stamp === this.appliedProductStamp) return;
    this.appliedProductStamp = stamp;
    if (!product) {
      this.errorMsg.set('No products available — agent tools need a product to manage.');
      return;
    }
    this.toolService.setProductContext(toProductContext(product));
    this.agentPromptService.setProductContext(toProductContext(product));
    this.pager.reset();
    this.loadTools();
  }

  loadTools(): void {
    this.loading.set(true);
    this.errorMsg.set('');
    this.toolService.listTools(this.includeInactive()).subscribe({
      next: (res) => {
        this.tools.set(res.tools ?? []);
        this.countChange.emit(res.count ?? res.tools?.length ?? 0);
        this.loading.set(false);
      },
      error: (err) => {
        this.tools.set([]);
        this.countChange.emit(0);
        this.errorMsg.set(err?.message ?? 'Failed to load tools.');
        this.loading.set(false);
      },
    });
  }

  toggleIncludeInactive(): void {
    this.includeInactive.set(!this.includeInactive());
    this.loadTools();
  }

  // ── panel ──────────────────────────────────────────────────────────────────

  openPanel(mode: ToolPanelMode, tool: AgentTool | null): void {
    this.panelMode.set(mode);
    this.selectedTool.set(tool);
    this.panelOpen.set(true);
  }

  closePanel(): void {
    this.panelOpen.set(false);
    this.selectedTool.set(null);
  }

  onSaved(): void {
    this.closePanel();
    this.loadTools();
  }

  // ── writes ─────────────────────────────────────────────────────────────────

  toggleActive(tool: AgentTool): void {
    this.toolService.patchTool(tool.id, { is_active: !tool.isActive }).subscribe({
      next: () => this.loadTools(),
      error: (err) => this.errorMsg.set(err?.message ?? 'Could not update active status.'),
    });
  }

  /** Row env selector changed — hold the move until the user confirms it. */
  requestEnvChange(tool: AgentTool, to: number | string): void {
    const target = Number(to);
    if (!target || target === tool.forEnv) return;
    this.envChangeTarget.set({ tool, to: target });
  }

  /** Reload so the row's selector snaps back to the unchanged value. */
  cancelEnvChange(): void {
    this.envChangeTarget.set(null);
    this.loadTools();
  }

  executeEnvChange(): void {
    const change = this.envChangeTarget();
    if (!change) return;
    this.changingEnv.set(true);
    this.toolService.patchTool(change.tool.id, { for_env: change.to }).subscribe({
      next: () => {
        this.changingEnv.set(false);
        this.envChangeTarget.set(null);
        this.loadTools();
      },
      error: (err) => {
        this.changingEnv.set(false);
        this.envChangeTarget.set(null);
        this.errorMsg.set(err?.message ?? 'Environment change failed.');
        this.loadTools();
      },
    });
  }

  confirmDelete(tool: AgentTool): void {
    this.deleteTarget.set(tool);
  }

  cancelDelete(): void {
    this.deleteTarget.set(null);
  }

  executeDelete(): void {
    const target = this.deleteTarget();
    if (!target) return;
    this.deleting.set(true);
    this.toolService.deleteTool(target.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.cancelDelete();
        this.loadTools();
      },
      error: (err) => {
        this.deleting.set(false);
        this.cancelDelete();
        this.errorMsg.set(err?.message ?? 'Delete failed.');
      },
    });
  }

  trackByTool(_: number, tool: AgentTool): number {
    return tool.id;
  }
}
